import styled, { keyframes } from "styled-components";
import { TableHeaderItem } from "../../../hooks/useTable";
import { Checkbox } from "../../atoms/Checkbox";
import { DeleteIcon } from "../../atoms/icons/DeleteIcon";
import { TableSortIcon } from "../../atoms/icons/TableSortIcon";

type Props<DataType> = {
  headers: TableHeaderItem<DataType>[];
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  download?: boolean;
  disableDeleteButton?: boolean;
  isChecked?: boolean;
  onSelectAll?: (checked: boolean) => void;
  onBulkDelete?: () => void;
  isSomeSelected?: boolean;
};

export const TableHeader = <DataType extends {}>({
  headers,
  selectable,
  editable,
  deletable,
  download,
  onSelectAll,
  onBulkDelete,
  disableDeleteButton,
  isChecked,
  isSomeSelected,
}: Props<DataType>) => {
  return (
    <thead>
      <tr>
        {selectable && (
          <th style={{ width: "24px" }}>
            <Checkbox
              name="table-all"
              variant="empty"
              onChange={onSelectAll}
              checked={isChecked}
              isSomeSelected={isSomeSelected}
            />
          </th>
        )}

        {headers.map((header, i) => (
          <th
            key={`table-header-${i}`}
            style={{ width: header.width || "auto" }}
          >
            <StyledTableHeadItem
              $isClickable={header.sortKey !== undefined}
              $isDisabled={header.disabled}
              onClick={header.disabled ? undefined : header.onClick}
            >
              {header.icon && <header.icon />}

              {header.title}

              {header.sortOrder && (
                <StyledArrowIcon $isOpen={header.sortOrder === "desc"}>
                  <TableSortIcon />
                </StyledArrowIcon>
              )}
            </StyledTableHeadItem>
          </th>
        ))}

        {editable && <th></th>}

        {deletable && (
          <th
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <StyledTableHeadItem
              $isClickable={true}
              $isDisabled={disableDeleteButton}
              onClick={() => !disableDeleteButton && onBulkDelete?.()}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <DeleteIcon />
              一括削除
            </StyledTableHeadItem>
          </th>
        )}
      </tr>
    </thead>
  );
};

const StyledTableHeadItem = styled.div<{
  $isClickable: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 4px;
  ${({ $isDisabled }) => $isDisabled && `opacity: 0.3;`}
  ${({ $isClickable, $isDisabled }) =>
    $isClickable && `cursor: ${$isDisabled ? "not-allowed" : "pointer"};`}
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
    $isOpen === null
      ? "rotate(0deg)"
      : $isOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;
